import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { shopifyAPI } from '../services/api';
import { format, endOfYear, subYears } from 'date-fns';

type GiftCardStatus = 'open' | 'expired' | 'deactivated';

const STATUS_BADGE: Record<GiftCardStatus, string> = {
  open: 'status-reconciled',
  expired: 'status-pending',
  deactivated: 'status-unmapped',
};

const formatAmount = (value: number | string, currency: string) =>
  `${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

const GiftCardReport: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [cards, setCards] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);

  // Default date: last day of last year (year-end closing)
  const defaultDate = format(endOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd');

  const urlDate = searchParams.get('date') || defaultDate;
  const urlStatus = searchParams.get('status') || '';

  const [dateInput, setDateInput] = useState(urlDate);
  const [statusFilter, setStatusFilter] = useState(urlStatus);

  useEffect(() => {
    setDateInput(urlDate);
    setStatusFilter(urlStatus);
  }, [urlDate, urlStatus]);

  const updateParams = useCallback((updates: { date?: string; status?: string }) => {
    const newParams = new URLSearchParams(searchParams);

    if (updates.date !== undefined) {
      if (updates.date && updates.date !== defaultDate) {
        newParams.set('date', updates.date);
      } else {
        newParams.delete('date');
      }
    }
    if (updates.status !== undefined) {
      if (updates.status) {
        newParams.set('status', updates.status);
      } else {
        newParams.delete('status');
      }
    }

    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, defaultDate]);

  const fetchReport = async (date?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await shopifyAPI.getGiftCards(date || dateInput);
      setCards(response.data.data);
      setSummary(response.data.summary);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load gift cards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(urlDate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredCards = useMemo(() => {
    if (!statusFilter) return cards;
    return cards.filter(card => card.status === statusFilter);
  }, [cards, statusFilter]);

  const currency = cards[0]?.currency || 'CHF';

  const handleRefresh = () => {
    updateParams({ date: dateInput, status: statusFilter });
    fetchReport(dateInput);
  };

  const handleStatusChange = (status: string) => {
    setStatusFilter(status);
    updateParams({ status });
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setMessage(null);
      const response = await shopifyAPI.syncGiftCards();
      setMessage(`Imported ${response.data.giftCardsImported} gift cards and ${response.data.transactionsImported} transactions`);
      await fetchReport(dateInput);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to sync gift cards');
    } finally {
      setSyncing(false);
    }
  };

  const handleExport = () => {
    shopifyAPI.exportGiftCards(dateInput, statusFilter || undefined);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Gift Cards (by date)</h1>
        <p>Remaining balance of all gift cards as of the selected date</p>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          alignItems: 'end'
        }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="asOfDate" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#4a5568' }}>
              As Of Date
            </label>
            <input
              type="date"
              id="asOfDate"
              className="form-control"
              value={dateInput}
              onChange={(e) => setDateInput(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="statusFilter" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#4a5568' }}>
              Status
            </label>
            <select
              id="statusFilter"
              className="form-control"
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="expired">Expired</option>
              <option value="deactivated">Deactivated</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={handleRefresh} disabled={loading}>
              Refresh
            </button>
            <button className="btn btn-secondary" onClick={handleSync} disabled={syncing}>
              {syncing ? 'Syncing...' : 'Sync from Shopify'}
            </button>
            {filteredCards.length > 0 && (
              <button className="btn btn-secondary" onClick={handleExport}>
                📥 Export CSV
              </button>
            )}
          </div>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}

      {summary && (
        <div className="stat-grid" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card">
            <h3>Open Gift Cards</h3>
            <div className="value" style={{ color: '#e53e3e' }}>
              {formatAmount(summary.open_total, currency)}
            </div>
            <div className="subvalue">{summary.open_count} cards with remaining balance</div>
          </div>

          <div className="stat-card">
            <h3>Expired</h3>
            <div className="value" style={{ color: '#d69e2e' }}>
              {formatAmount(summary.expired_total, currency)}
            </div>
            <div className="subvalue">{summary.expired_count} cards, not yet deactivated</div>
          </div>

          <div className="stat-card">
            <h3>Deactivated</h3>
            <div className="value" style={{ color: '#718096' }}>
              {formatAmount(summary.deactivated_total, currency)}
            </div>
            <div className="subvalue">{summary.deactivated_count} cards</div>
          </div>
        </div>
      )}

      <div className="card">
        <h2>
          Gift Cards{' '}
          {filteredCards.length > 0 && (
            <span style={{ fontWeight: 400, fontSize: '1rem', color: '#718096' }}>({filteredCards.length} total)</span>
          )}
        </h2>

        {loading ? (
          <div className="loading">Loading gift cards...</div>
        ) : filteredCards.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#718096' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>No gift cards with a remaining balance</div>
            <div style={{ marginTop: '0.5rem' }}>
              As of {format(new Date(urlDate), 'MMMM dd, yyyy')}. Run "Sync from Shopify" if gift cards were issued recently.
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Gift Card</th>
                  <th>Issued</th>
                  <th>Expires</th>
                  <th>Initial Value</th>
                  <th>Redeemed by Date</th>
                  <th>Balance by Date</th>
                  <th>Status</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {filteredCards.map((card: any) => (
                  <tr key={card.id}>
                    <td style={{ fontFamily: 'monospace' }}>
                      {card.last_characters ? `...${card.last_characters}` : card.shopify_gift_card_id}
                    </td>
                    <td>{format(new Date(card.shopify_created_at), 'MMM dd, yyyy')}</td>
                    <td>{card.expires_on ? format(new Date(`${card.expires_on}T00:00:00`), 'MMM dd, yyyy') : '—'}</td>
                    <td>{formatAmount(card.initial_value, card.currency)}</td>
                    <td style={{ color: Number(card.redeemed_as_of_date) > 0 ? '#ed8936' : '#a0aec0' }}>
                      {Number(card.redeemed_as_of_date) > 0 ? formatAmount(card.redeemed_as_of_date, card.currency) : '—'}
                    </td>
                    <td style={{ fontWeight: 600 }}>{formatAmount(card.balance_as_of_date, card.currency)}</td>
                    <td>
                      <span className={`status-badge ${STATUS_BADGE[card.status as GiftCardStatus]}`}>{card.status}</span>
                    </td>
                    <td>{card.note || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Understanding This Report</h2>
        <div style={{ lineHeight: '1.8' }}>
          <p><strong>Balance by Date:</strong> Initial value minus all redemptions up to the end of the selected day. Gift cards issued after that day are not listed.</p>
          <p><strong>Expired:</strong> The expiry date lies before the selected date, but the card was not deactivated. The balance is shown separately and is not part of the open total.</p>
          <p><strong>Deactivated:</strong> The card was deactivated on or before the selected date. Its balance is shown separately and is not part of the open total.</p>
        </div>
      </div>
    </div>
  );
};

export default GiftCardReport;
